import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check } from "lucide-react";

const IaGenerator = ({
  iaForm,
  setIaForm,
  iaSteps,
  iaLoading,
  iaResult,
  iaError,
  handleIaSubmit,
  setCurrentSection
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Lado esquerdo - Formulário */}
        <div className="space-y-6">
          <div>
            <span className="inline-flex items-center rounded-full border border-pink-500/60 bg-pink-500/10 px-3 py-1.5 text-xs font-medium tracking-wide mb-4">
              TECNOLOGIA EXCLUSIVA CLICKHUB
            </span>
            <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              IA Cria Tudo Automaticamente
            </h1>
            <p className="text-gray-400 text-lg mb-2">
              Veja a mágica acontecer: nossa IA cria título, foto, descrição, vídeo e cadastro nos marketplaces.
            </p>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 text-sm">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-semibold">100% Automático</span>
            </div>
          </div>

          <form
            onSubmit={handleIaSubmit}
            className="space-y-4 bg-gray-900/60 border border-gray-700/60 rounded-2xl p-6 shadow-2xl"
            data-testid="ia-form"
          >
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome do Produto
              </label>
              <Input
                required
                value={iaForm.nome_produto}
                onChange={(e) => setIaForm({...iaForm, nome_produto: e.target.value})}
                placeholder="Ex: Escova de Dente Elétrica Profissional"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="ia-produto"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Categoria
              </label>
              <Input
                required
                value={iaForm.categoria}
                onChange={(e) => setIaForm({...iaForm, categoria: e.target.value})}
                placeholder="Ex: Higiene Bucal"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Plataforma
                </label>
                <select
                  value={iaForm.plataforma}
                  onChange={(e) => setIaForm({...iaForm, plataforma: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="mercado_livre">Mercado Livre</option>
                  <option value="shopee">Shopee</option>
                  <option value="amazon">Amazon</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Público-alvo (opcional)
                </label>
                <Input
                  value={iaForm.publico_alvo}
                  onChange={(e) => setIaForm({...iaForm, publico_alvo: e.target.value})}
                  placeholder="Ex: Adultos, mães..."
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Benefícios (um por linha)
              </label>
              <textarea
                value={iaForm.beneficios}
                onChange={(e) => setIaForm({...iaForm, beneficios: e.target.value})}
                placeholder="Remoção de placa&#10;Timer inteligente&#10;Bateria recarregável"
                rows={4}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-pink-500 resize-vertical"
              />
            </div>

            {iaError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                {iaError}
              </div>
            )}

            <Button
              type="submit"
              disabled={iaLoading}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400 text-white font-semibold py-3 shadow-lg shadow-pink-500/30"
              data-testid="ia-generate-btn"
            >
              {iaLoading ? "IA trabalhando..." : "Criar anúncio automaticamente"}
            </Button>
          </form>
        </div>

        {/* Lado direito - Steps e Resultado */}
        <div className="space-y-6">
          {/* Pipeline de IA */}
          <div className="relative bg-gray-900/70 border border-gray-700/70 rounded-3xl p-6 overflow-hidden shadow-2xl">
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-pink-500/20 blur-3xl rounded-full pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-purple-500/20 blur-3xl rounded-full pointer-events-none" />

            <div className="relative space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Pipeline de IA ClickHub</h2>
                <span className="text-xs px-3 py-1 rounded-full bg-gray-800/60 border border-gray-700 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  Robô trabalhando 24/7
                </span>
              </div>

              <div className="space-y-3">
                {iaSteps.map((step) => (
                  <div
                    key={step.id}
                    className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm border transition-all ${
                      step.status === "active"
                        ? "border-pink-500/70 bg-pink-500/10 scale-105"
                        : step.status === "done"
                        ? "border-emerald-500/60 bg-emerald-500/5"
                        : "border-gray-700/70 bg-gray-800/80"
                    }`}
                    data-testid={`ia-step-${step.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.status === "done"
                            ? "bg-emerald-500 text-white"
                            : step.status === "active"
                            ? "bg-pink-500 text-white animate-pulse"
                            : "bg-gray-700 text-gray-400"
                        }`}
                      >
                        {step.status === "done" ? "✓" : step.status === "active" ? "⟳" : "○"}
                      </div>
                      <span className={step.status === "active" ? "font-semibold" : ""}>
                        {step.label}
                      </span>
                    </div>

                    <span className="text-xs text-gray-400">
                      {step.status === "done"
                        ? "Concluído"
                        : step.status === "active"
                        ? "Em andamento..."
                        : "Aguardando"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Resultado */}
          {iaResult && (
            <div className="bg-gray-900/70 border border-gray-700/70 rounded-3xl p-6 shadow-xl" data-testid="ia-result">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-400" />
                Anúncio Gerado com Sucesso!
              </h3>

              {iaResult.titulo?.data && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Título Recomendado:</h4>
                  <p className="text-white bg-gray-800/50 rounded-lg p-3">
                    {iaResult.titulo.data.titulo_recomendado}
                  </p>
                </div>
              )}

              {iaResult.descricao?.data && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Bullets:</h4>
                  <ul className="space-y-1">
                    {iaResult.descricao.data.bullets?.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {iaResult.descricao?.data?.descricao_longa && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-300 mb-2">Descrição:</h4>
                  <p className="text-sm text-gray-300 bg-gray-800/50 rounded-lg p-3 whitespace-pre-line">
                    {iaResult.descricao.data.descricao_longa}
                  </p>
                </div>
              )}

              <Button
                onClick={() => setCurrentSection("integrations")}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                Publicar no Marketplace
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IaGenerator;